import { combineReducers } from 'redux';
import { call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';

import { environment } from 'environments/environment';
import { CustomerAccountBasic, CustomerAccountDetail } from 'generated/bffCustomerAccount/models';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionType,
  createApiActionCreators,
  createReducer,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { ApiClientRequest } from 'models/ApiClientRequest';
import {
  CustomerAccountInfoListRequestPayload,
  CustomerAccountInfoListReducerPayload,
} from 'models/context/CustomerAccountInfo';
import { CustomerInfo } from 'models/context/CustomerInfo';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { selectContextCustomerInfo } from 'saga/context/customer/ducks';
import { api, api as customerAccountApi } from 'saga/context/customerAccount/api';
import { ContextCustomerAccountActionTypes } from 'saga/context/customerAccount/ducks';

/* STATE */
export interface CustomerAccountState {
  customerAccountDetail: CustomerAccountDetail | null;
  fullCustomerAccountDetailFromContext: CustomerAccountDetail | null;
  customerAccountList: CustomerAccountBasic[] | null;
}

/* ACTION TYPES */
export enum CustomerAccountActionTypes {
  GetCustomerAccountDetail = '@@CustomerAccount/GET_CUSTOMER_ACCOUNT',
  GetCustomerAccountList = '@@CustomerAccount/GET_CUSTOMER_ACCOUNT_LIST',
}

/* ACTIONS */
export const getCustomerAccountDetailActions = createApiActionCreators(
  CustomerAccountActionTypes.GetCustomerAccountDetail
);
export const getCustomerAccountListActions = createApiActionCreators(CustomerAccountActionTypes.GetCustomerAccountList);

/* REDUCERS */
const initialState: CustomerAccountState = {
  customerAccountDetail: null,
  fullCustomerAccountDetailFromContext: null,
  customerAccountList: null,
};

const customerAccountDetail = createReducer(initialState.customerAccountDetail, {
  [CustomerAccountActionTypes.GetCustomerAccountDetail]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerAccountDetail | null, payload: CustomerAccountDetail) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
});

const fullCustomerAccountDetailFromContext = createReducer(initialState.fullCustomerAccountDetailFromContext, {
  [ContextCustomerAccountActionTypes.SetContextCustomerAccount]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerAccountDetail | null, payload: CustomerAccountDetail) => payload,
    [RequestActionTypes.FAILURE]: () => null,
  },
});
const customerAccountList = createReducer(initialState.customerAccountList, {
  [CustomerAccountActionTypes.GetCustomerAccountList]: {
    [RequestActionTypes.SUCCESS]: (_state: CustomerAccountBasic[], payload: CustomerAccountInfoListReducerPayload) => {
      if (payload.page && payload.page > 1) {
        return [..._state, ...payload.customerAccountBasicList];
      }

      return payload.customerAccountBasicList || [];
    },
    [RequestActionTypes.FAILURE]: () => null,
  },
});

export default combineReducers<CustomerAccountState>({
  customerAccountDetail,
  fullCustomerAccountDetailFromContext,
  customerAccountList,
});

/* SELECTORS */

const selectCustomerAccount = (state: AppState) => state.customerAccount;

export const selectCustomerAccountDetail = (state: AppState): CustomerAccountDetail | null =>
  selectCustomerAccount(state).customerAccountDetail;
export const selectFullCustomerAccountDetailFromContext = (state: AppState): CustomerAccountDetail | null =>
  selectCustomerAccount(state).fullCustomerAccountDetailFromContext;
export const selectCustomerAccountList = (state: AppState): CustomerAccountBasic[] | null =>
  selectCustomerAccount(state).customerAccountList;

/* SAGAS */
function* getCustomerAccountDetail({ payload: caRefNo }: AppAction<string>) {
  const contextCustomerAccount: CustomerAccountDetail | null = yield select(selectFullCustomerAccountDetailFromContext);
  const customerAccount: CustomerAccountDetail | null = yield select(selectCustomerAccountDetail);

  if (customerAccount?.caRefNo === caRefNo) {
    return;
  }

  if (contextCustomerAccount?.caRefNo === caRefNo) {
    yield put(getCustomerAccountDetailActions.success(contextCustomerAccount));
  } else {
    const request: ApiClientRequest = {
      apiCall: call(
        customerAccountApi.getCustomerAccount,
        yield getApiUrl(environment.urlConfig.customerAccount),
        caRefNo
      ),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      yield put(getCustomerAccountDetailActions.success(resp.data));
    } else {
      yield put(getCustomerAccountDetailActions.failure());
    }
  }
}

function* getCustomerAccountList({ payload }: AppAction<CustomerAccountInfoListRequestPayload>) {
  const contextCustomer: CustomerInfo | null = yield select(selectContextCustomerInfo);

  if (!contextCustomer) {
    return;
  }

  const request: ApiClientRequest = {
    apiCall: call(
      api.getCustomerAccountList,
      yield getApiUrl(environment.urlConfig.customerAccount),
      contextCustomer.cuRefNo,
      payload?.onlyActivatedCa,
      payload?.page
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getCustomerAccountListActions.success({
        customerAccountBasicList: resp.data?.customerAccountBasicList || [],
        page: payload?.page,
      })
    );

    if (resp.data.totalPages > resp.data.currentPage) {
      yield put(
        getCustomerAccountListActions.request({
          onlyActivatedCa: payload?.onlyActivatedCa,
          page: resp.data.currentPage + 1,
        })
      );
    }
  } else {
    yield put(getCustomerAccountListActions.failure());
  }
}

/* EXPORT */
export function* customerAccountSaga() {
  yield takeLatest(
    createActionType(CustomerAccountActionTypes.GetCustomerAccountDetail, RequestActionTypes.REQUEST),
    getCustomerAccountDetail
  );

  yield takeEvery(
    createActionType(CustomerAccountActionTypes.GetCustomerAccountList, RequestActionTypes.REQUEST),
    getCustomerAccountList
  );
}
